import { ReactNode, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { EMAIL } from 'constants/regex'
import { routes } from 'types/routes'
import { requestNewPassword, resetPassword } from 'store/auth/thunks'
import { ResetPasswordFormInputs, ResetPasswordRouteParams } from './types'
import styles from '../Auth.module.scss'
import NeedHelp from 'pages/auth/components/need-help/NeedHelp'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { Banner, Button, Form, Heading, Password, TextField } from '@boltenergy-be/design-system'
import Link from 'components/link/Link.tsx'
import { matchPath, useParams } from 'react-router'
import { IS_BETA } from 'constants/envs.ts'
import { UserTypes } from 'store/auth/types.ts'
import { determineAuthUserType } from 'pages/auth/utils.ts'
import { getUserTypeLoginPage } from 'utils/user.ts'
import { trackEvent } from 'utils/tracking.ts'
import { ERROR_CODES } from '@boltenergy-be/shared'
import { useGetLanguage } from 'hooks/use-get-language'

const ResetPassword = () => {
  // REDUX STORE
  const { loading } = useStoreSelector((store) => store.auth)
  const dispatch = useStoreDispatch()

  // Local state
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)

  // React hook form
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<ResetPasswordFormInputs>({ mode: 'onBlur' })
  const watchPassword = watch('password')

  // i18n
  const { t } = useTranslation(['auth', 'errors', 'validation'])
  const language = useGetLanguage()

  // React router
  const navigate = useNavigate()
  const { token } = useParams<ResetPasswordRouteParams>()
  const { pathname } = useLocation()

  // Constants
  const isActivate =
    matchPath(routes.ACTIVATE, pathname) || matchPath(routes.ACTIVATE_SALES, pathname) || matchPath(routes.ACTIVATE_SUPER_USER, pathname)
  const isRequestNewPassword = [
    routes.REQUEST_NEW_PASSWORD,
    routes.REQUEST_NEW_PASSWORD_SALES,
    routes.REQUEST_NEW_PASSWORD_SUPER_USER
  ].includes(pathname as routes)
  const translationKey = isActivate ? 'activate' : isRequestNewPassword ? 'requestNewPassword' : 'resetPassword'
  const userType = determineAuthUserType(pathname)

  // Reset error on first render of request new password page.
  // Expired tokens throw 401-001 error message and navigates to isRequestNewPassword. Error would otherwise persist.
  useEffect(() => {
    if (isRequestNewPassword) setError(null)
  }, [isRequestNewPassword])

  // Redirect to login if in beta (reset password page inaccessible on beta)
  useEffect(() => {
    if (IS_BETA) {
      void navigate(routes.LOGIN)
    }
  }, [])

  /**
   * Handles the form submit after validation
   *
   * @param {ResetPasswordFormInputs} data
   */
  const onSubmit = async (data: ResetPasswordFormInputs) => {
    setError(null)
    setSuccess(false)

    let dispatchResult

    // Check if send reset password mail page...
    if (isRequestNewPassword) {
      trackEvent({ action: 'send', category: 'auth', object: 'reset_password_mail' })

      const { email } = data

      dispatchResult = await dispatch(requestNewPassword({ email: email?.toLowerCase() ?? '', userType }))
    }

    // ...or the reset password page itself
    else {
      trackEvent({ action: 'submit', category: 'auth', object: 'reset_password' })

      const { password } = data

      dispatchResult = await dispatch(
        resetPassword({
          token: token ?? '',
          password: password ?? '',
          userType
        })
      )
    }

    if (dispatchResult.meta.requestStatus === 'fulfilled') {
      setSuccess(true)

      if (!isRequestNewPassword) {
        void navigate(getUserTypeLoginPage(userType))
      }
    } else {
      const error = dispatchResult?.payload
      setError(typeof error === 'string' ? error : 'error')
    }
  }

  /**
   * Mapping for specific errors with default error as fallback
   * @param {string} err
   * @returns {string | React.ReactNode}
   */
  const getErrorMessage = (err: string): string | ReactNode => {
    switch (err) {
      case ERROR_CODES.expired:
        return (
          <Trans
            ns="errors"
            i18nKey={($) => $.bolt.passwordTokenExpired}
            components={{ RequestNew: <Link variant="inline" href={routes.REQUEST_NEW_PASSWORD} /> }}
          />
        )

      default:
        return t(($) => $.default, {
          ns: 'errors'
        })
    }
  }

  return (
    <>
      <div className={styles['auth-body']}>
        <header className={styles.header}>
          <Heading as="h1" variant="h3">
            {t(($) => $.titles[translationKey])}
          </Heading>
          <p className={styles.description}>{t(($) => $.descriptions[translationKey])}</p>
        </header>

        <Form onSubmit={handleSubmit(onSubmit)}>
          {isRequestNewPassword ? (
            <TextField
              id="email"
              type="email"
              autoComplete="off"
              // Disable lastpass autofill
              data-lpignore="true"
              label={t(($) => $.fields.email)}
              {...register('email', {
                required: t(($) => $.required, {
                  ns: 'validation'
                }),
                pattern: {
                  value: EMAIL,
                  message: t(($) => $.invalid.email, {
                    ns: 'validation'
                  })
                }
              })}
              error={errors?.email?.message}
            />
          ) : (
            <>
              <Password
                id="password"
                label={t(($) => $.fields.password)}
                autoComplete="new-password"
                {...register('password', {
                  required: t(($) => $.required, {
                    ns: 'validation'
                  }),
                  minLength: {
                    value: 8,
                    message: t(($) => $.invalid.passwordMinimum, {
                      min: 8,
                      ns: 'validation'
                    })
                  }
                })}
                error={errors?.password?.message}
                {...{ language }}
              />

              <Password
                id="confirmPassword"
                autoComplete="new-password"
                label={t(($) => $.fields.confirmPassword)}
                {...register('confirmPassword', {
                  required: t(($) => $.required, {
                    ns: 'validation'
                  }),
                  validate: (value) =>
                    value === watchPassword ||
                    t(($) => $.invalid.passwordConfirm, {
                      ns: 'validation'
                    })
                })}
                error={errors?.confirmPassword?.message}
                {...{ language }}
              />
            </>
          )}

          {!success && (
            <Button isFullwidth loading={loading} disabled={success || !!error}>
              {t(($) => $.buttons[translationKey])}
            </Button>
          )}
        </Form>

        {!loading && (
          <>
            {success && (
              <Banner className={styles.banner} type="positive">
                {t(($) => $.messages.successEmail)}
              </Banner>
            )}

            {error && (
              <Banner className={styles.banner} type="blocking">
                {getErrorMessage(error)}
              </Banner>
            )}
          </>
        )}

        <ul className={styles['links-list']}>
          <li>
            <Link
              size="medium"
              variant="secondary"
              href={
                userType === UserTypes.SALES
                  ? routes.LOGIN_SALES
                  : userType === UserTypes.SUPER_USER
                    ? routes.LOGIN_SUPER_USER
                    : routes.LOGIN
              }
            >
              {t(($) => $.buttons.backToLogin)}
            </Link>
          </li>
        </ul>
      </div>

      <footer className={styles['auth-footer']}>
        <NeedHelp />
      </footer>
    </>
  )
}

export default ResetPassword
